/**
 * GraphSearch — search for words that exist on the graph.
 *
 * Desktop: always-visible text input in the header right column.
 * Mobile (<=540px): a search icon button that toggles the input.
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';

export default function GraphSearch({ nodes, onSearch }) {
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const inputRef = useRef(null);

  // Build a Set of lowercase node IDs for fast lookup
  const nodeIds = useMemo(
    () => new Set(nodes.map(n => n.id.toLowerCase())),
    [nodes],
  );

  // Filter matches as user types
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return nodes
      .filter(n => n.id.toLowerCase().includes(q))
      .map(n => n.id)
      .slice(0, 6);
  }, [query, nodes]);

  const selectWord = useCallback((word) => {
    onSearch(word);
    setQuery('');
    setMobileOpen(false);
  }, [onSearch]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && matches.length > 0) {
      selectWord(matches[0]);
    }
    if (e.key === 'Escape') {
      setQuery('');
      setMobileOpen(false);
      onSearch(null);
    }
  }, [matches, selectWord, onSearch]);

  const toggleMobile = useCallback(() => {
    setMobileOpen(prev => {
      if (prev) {
        // Closing: clear search
        setQuery('');
        onSearch(null);
      }
      return !prev;
    });
  }, [onSearch]);

  // Auto-focus input when mobile search opens
  useEffect(() => {
    if (mobileOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mobileOpen]);

  return (
    <div className="graph-search">
      {/* Mobile toggle button (hidden on desktop via CSS) */}
      <button
        className="graph-search-toggle"
        onClick={toggleMobile}
        aria-label="Search graph"
      >
        {mobileOpen ? '\u00d7' : '\u2315'}
      </button>

      {/* Search input + dropdown */}
      <div className={`graph-search-input-wrap ${mobileOpen ? 'mobile-open' : ''}`}>
        <input
          ref={inputRef}
          className="graph-search-input"
          type="text"
          placeholder="Search graph..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck="false"
        />
        {query && matches.length > 0 && (
          <div className="graph-search-dropdown">
            {matches.map(word => (
              <button
                key={word}
                className="graph-search-result"
                onClick={() => selectWord(word)}
              >
                {word.replace(/\b\w/g, c => c.toUpperCase())}
              </button>
            ))}
          </div>
        )}
        {query && matches.length === 0 && (
          <div className="graph-search-dropdown">
            <div className="graph-search-empty">Not on graph</div>
          </div>
        )}
      </div>
    </div>
  );
}
